import { Box, Stack, Typography } from '@dtx-company/design-system/src'
import { CardBase, LabelText } from './subcomponents'
import { EditTheme } from './types'
import { FC } from 'react'
import { STYLE } from '../../../constants/customizeTheme'
import { SelectorFrame } from './SelectorFrame'
import { Tonality } from '@mui/icons-material'
import { calcContrastText } from './utils'
import { getBackgroundColor, getColor } from '../../../utils/theme'
import { stopPropagationEvents } from '../PageEditor/components/LinkEditDrawer/LinkEditDrawer.utils'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useCurrentPagePermissions } from '../../../utils/useGetCurrentPagePermissions'
import events from '@dtx-company/inter-app/src/event-tracking/events/flowpage'

interface CardProps {
  handleSelect: (values: EditTheme) => void
  curr: string
  color?: string
  mobile?: boolean
  disabled?: boolean
}

export const ShadePicker: FC<CardProps> = ({ handleSelect, curr, color, disabled }) => {
  const { isViewer } = useAuthState()
  const { hasViewOnlyAccess } = useCurrentPagePermissions()
  const canUserEditShade = !(isViewer || hasViewOnlyAccess) && !disabled
  return (
    <Stack
      sx={{
        width: '100%',
        px: { xs: 2, md: 'unset' }
      }}
      {...stopPropagationEvents}
    >
      <Stack
        sx={{
          opacity: canUserEditShade ? 1 : 0.35
        }}
        direction="row"
        alignItems="center"
        spacing={{ md: 2 }}
      >
        <Tonality sx={{ display: { xs: 'none', md: 'flex', fontSize: 16 } }} />
        <Typography variant={{ xs: 'bodyMedium', md: 'subtitleSmall' }}>Choose a shade</Typography>
      </Stack>
      <CardBase padding={'0px'} justify="space-between" backgroundColor="transparent">
        {Object.entries(STYLE).map(([shade, _], idx) => {
          const selected = shade === curr
          return (
            <Box
              key={idx}
              onClick={() => {
                if (canUserEditShade) {
                  handleSelect({ style: shade })
                  events.userChangedStyle()
                }
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: canUserEditShade ? 1 : 0.35,
                cursor: canUserEditShade ? 'pointer' : 'not-allowed'
              }}
            >
              <Box
                key={shade}
                sx={theme => ({
                  display: 'flex',
                  justifyContent: 'center',
                  position: 'relative',
                  backgroundColor: `${getBackgroundColor(getColor(color), shade)}`,
                  borderRadius: '50%',
                  border: `1px solid ${theme.palette.border.outline}`,
                  width: { xs: theme.spacing(10), lg: theme.spacing(12) },
                  height: { xs: theme.spacing(10), lg: theme.spacing(12) },
                  mb: theme.spacing(2),
                  span: {
                    alignSelf: 'center'
                  }
                })}
                {...stopPropagationEvents}
              >
                <Typography variant="bodySmall" color={calcContrastText(color, shade)}>
                  Aa
                </Typography>

                {selected && <SelectorFrame />}
              </Box>
              <LabelText selected={selected} label={shade} />
            </Box>
          )
        })}
      </CardBase>
    </Stack>
  )
}
